/**
 * Scope prefixes for the ⌘K palette: `repo:foo`, `secret:MONGO`, `ns:qa`.
 *
 * Anyone who lives in a command palette reaches for these first, and they're
 * cheap: the resolver already accepts an entityType filter, so parsing happens
 * entirely in the input.
 *
 * Shared with the full-results page so the palette and `/search` agree on what a
 * prefix means — if they parsed separately, `ns:` would eventually mean one thing
 * in one place and nothing in the other.
 */
export const SCOPE_ALIASES: Record<string, string> = {
  repo: 'repo',
  repos: 'repo',
  secret: 'secret',
  secrets: 'secret',
  cluster: 'cluster',
  clusters: 'cluster',
  ns: 'namespace',
  namespace: 'namespace',
  workload: 'service', // workloads aren't indexed; a service is the closest real answer
  service: 'service',
  catalog: 'catalog',
};

export interface ParsedQuery {
  /** entity_type to filter on, or null for all types. */
  scope: string | null;
  /** The alias the user actually typed, for the chip label. */
  alias: string | null;
  /** The query with the prefix removed. */
  term: string;
}

/**
 * Splits `secret:MONGO_URL` into `{ scope: 'secret', term: 'MONGO_URL' }`.
 * An unknown prefix is NOT treated as a scope — `foo:bar` stays a literal search,
 * because a colon is a normal character in the things this app indexes (image
 * tags, ports) and silently dropping half the query would be worse than ignoring
 * the prefix.
 */
export function parseScopedQuery(input: string): ParsedQuery {
  const m = /^([a-z]+):\s*(.*)$/is.exec(input.trim());
  if (!m) return { scope: null, alias: null, term: input.trim() };
  const alias = m[1].toLowerCase();
  const scope = SCOPE_ALIASES[alias];
  if (!scope) return { scope: null, alias: null, term: input.trim() };
  return { scope, alias, term: m[2].trim() };
}
